import {
  Drawer,
  Panel,
  PanelGroup,
  PanelResizeHandle,
  Text,
} from '@southfields-digital/mxmz-react-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CrossListDndProvider } from 'src/components/DndKitSortableList';
import { StatusDescription } from 'src/components/GoLive';
import { getMappedCanvases } from 'src/components/GoLive/utils';
import GraphicDetails from 'src/components/GraphicDetails';
import GraphicList from 'src/components/GraphicList/GraphicList';
import Graphics from 'src/components/Graphics';
import PlaylistActionBar from 'src/components/PlaylistActionBar/PlaylistActionBar';
import PlaylistSelector from 'src/components/Playlists/PlaylistSelector';
import QuickControl from 'src/components/QuickControl/QuickControl';
import RundownDialog, { DialogTypes } from 'src/components/RundownDialog';
import ViewWindow from 'src/components/ViewWindow/ViewWindow';
import { StateType } from 'src/redux/reducers';
import {
  REQUEST_CREATE_PLAYLIST,
  REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
  REQUEST_SET_SELECTED_PLAYLIST,
  REQUEST_UPDATE_PLAYLIST,
} from 'src/redux/reducers/playlist';
import { REQUEST_CREATE_PLAYLIST_GRAPHIC } from 'src/redux/reducers/playlistGraphic';
import { getPlaylistMode } from 'src/utils/playlist';

import styles from '../RundownDetail.module.scss';
import ToggleButtons from 'src/components/ToggleButtons/ToggleButtons';

function LayoutNrcsOperator() {
  const { rundownId } = useParams<{ rundownId: string }>();
  const { data, outputs } = useSelector((state: StateType) => state.rundown.detail);

  const dispatch = useDispatch();
  const { data: playlists } = useSelector((state: StateType) => state.playlist.getAll);
  const { selectedPlaylistGraphic } = useSelector((state: StateType) => state.playlistGraphic);

  const [leftPlaylistId, setLeftPlaylistId] = useState<string | null>(null);
  const [rightPlaylistId, setRightPlaylistId] = useState<string | null>(null);
  const [leftEditable, setLeftEditable] = useState(true);
  const [rightEditable, setRightEditable] = useState(true);
  const [selectedCanvas, setSelectedCanvas] = useState<RundownTemplateCanvas | undefined>();
  const [drawerTargetPlaylistId, setDrawerTargetPlaylistId] = useState<string | null>(null);
  const [editPlaylistId, setEditPlaylistId] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState<DialogTypes | null>(null);

  const { connected: mosConnected, autoFetch: mosAutoFetch } = useSelector(
    (state: StateType) => state.mos
  );

  // Fetch playlists for this rundown
  useEffect(() => {
    if (rundownId) {
      dispatch({
        type: REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
        payload: { rundownId, firstSelected: true },
      });
    }
  }, [dispatch, rundownId]);

  // Auto-select first playlist for columns that haven't been set yet
  useEffect(() => {
    if (!playlists?.length) return;

    if (!leftPlaylistId) {
      setLeftPlaylistId(playlists[0].id);
    }
    if (!rightPlaylistId) {
      setRightPlaylistId(playlists[0].id);
    }
  }, [playlists]);

  const handleSelectLeft = useCallback(
    (playlistId: string) => {
      setLeftPlaylistId(playlistId);
      dispatch({ type: REQUEST_SET_SELECTED_PLAYLIST, payload: { id: playlistId } });
    },
    [dispatch]
  );

  const handleSelectRight = useCallback(
    (playlistId: string) => {
      setRightPlaylistId(playlistId);
      dispatch({ type: REQUEST_SET_SELECTED_PLAYLIST, payload: { id: playlistId } });
    },
    [dispatch]
  );

  const handleGraphicSelected = useCallback(
    (graphicId: string) => {
      dispatch({
        type: REQUEST_CREATE_PLAYLIST_GRAPHIC,
        payload: { playlistId: drawerTargetPlaylistId, graphicId },
      });
      setDrawerTargetPlaylistId(null);
    },
    [dispatch, drawerTargetPlaylistId]
  );

  const getEffectiveEditable = useCallback(
    (playlistId: string | null, localEditable: boolean) => {
      const playlist = playlists?.find((p) => p.id === playlistId);
      if (getPlaylistMode(playlist) === 'mos') return !mosConnected || !mosAutoFetch;
      return localEditable;
    },
    [playlists, mosConnected, mosAutoFetch]
  );

  const leftEffectiveEditable = getEffectiveEditable(leftPlaylistId, leftEditable);
  const rightEffectiveEditable = getEffectiveEditable(rightPlaylistId, rightEditable);

  const leftPlaylistMode = useMemo(() => {
    const playlist = playlists?.find((p) => p.id === leftPlaylistId);
    return getPlaylistMode(playlist);
  }, [playlists, leftPlaylistId]);

  const rightPlaylistMode = useMemo(() => {
    const playlist = playlists?.find((p) => p.id === rightPlaylistId);
    return getPlaylistMode(playlist);
  }, [playlists, rightPlaylistId]);

  const handleRundownDialog = useCallback(
    (name: string) => {
      if (editPlaylistId) {
        dispatch({ type: REQUEST_UPDATE_PLAYLIST, payload: { id: editPlaylistId, name } });
        setEditPlaylistId(null);
      } else if (showDialog === DialogTypes.PLAYLIST) {
        dispatch({ type: REQUEST_CREATE_PLAYLIST, payload: { rundownId, name } });
      }
      setShowDialog(null);
    },
    [dispatch, editPlaylistId, showDialog, rundownId]
  );

  const handleEditPlaylist = useCallback((playlistId: string | null) => {
    if (!playlistId) return;
    setEditPlaylistId(playlistId);
    setShowDialog(DialogTypes.PLAYLIST);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setShowDialog(null);
    setEditPlaylistId(null);
  }, []);

  const handleOpenCreateDialog = useCallback(() => {
    setShowDialog(DialogTypes.PLAYLIST);
  }, []);

  const handleOpenLeftDrawer = useCallback(() => {
    setDrawerTargetPlaylistId(leftPlaylistId);
  }, [leftPlaylistId]);

  const handleOpenRightDrawer = useCallback(() => {
    setDrawerTargetPlaylistId(rightPlaylistId);
  }, [rightPlaylistId]);

  const handleEditLeftPlaylist = useCallback(() => {
    handleEditPlaylist(leftPlaylistId);
  }, [handleEditPlaylist, leftPlaylistId]);

  const handleEditRightPlaylist = useCallback(() => {
    handleEditPlaylist(rightPlaylistId);
  }, [handleEditPlaylist, rightPlaylistId]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerTargetPlaylistId(null);
  }, []);

  useEffect(() => {
    if (data?.isLive && !selectedCanvas) {
      const activeCanvas = data.canvases.find(
        (canvas: RundownTemplateCanvas) => canvas.output && canvas.graphics.length
      );
      if (activeCanvas) {
        setSelectedCanvas(activeCanvas);
      }
    }
  }, [data]);

  if (!data || !rundownId) return null;

  const liveCanvases = data.canvases.filter((canvas: RundownTemplateCanvas) => canvas.output);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <RundownDialog
        key={editPlaylistId || 'create'}
        dialogTitle={editPlaylistId ? 'Rename Playlist' : 'Create Playlist'}
        dialogSubtitle={
          editPlaylistId ? 'Enter a new name for the playlist' : 'Enter name for the playlist'
        }
        isOpen={showDialog === DialogTypes.PLAYLIST}
        initialValue={
          editPlaylistId ? playlists?.find((p) => p.id === editPlaylistId)?.name : undefined
        }
        onClose={handleCloseDialog}
        onSubmit={handleRundownDialog}
      />

      <div className="flex-1 min-h-0">
        <PanelGroup className={styles.Container} direction="horizontal">
          <CrossListDndProvider>
            {/* Left column - Primary playlist */}
            <Panel defaultSize={32} minSize={20}>
              <PanelGroup direction="vertical">
                <div className="flex flex-col p-3 gap-4">
                  <PlaylistSelector
                    value={leftPlaylistId}
                    onChange={handleSelectLeft}
                    onAdd={handleOpenCreateDialog}
                  />
                  <PlaylistActionBar
                    playlistId={leftPlaylistId}
                    mode={leftPlaylistMode}
                    editable={leftEffectiveEditable}
                    onToggleEditable={setLeftEditable}
                    onAddGraphic={handleOpenLeftDrawer}
                    onEdit={handleEditLeftPlaylist}
                  />
                </div>
                <Panel defaultSize={100}>
                  <Graphics playlistId={leftPlaylistId} editable={leftEffectiveEditable} />
                </Panel>
              </PanelGroup>
            </Panel>

            <PanelResizeHandle />

            {/* Middle column - Secondary playlist */}
            <Panel defaultSize={32} minSize={20}>
              <PanelGroup direction="vertical">
                <div className="flex flex-col p-3 gap-4">
                  <PlaylistSelector
                    value={rightPlaylistId}
                    onChange={handleSelectRight}
                    onAdd={handleOpenCreateDialog}
                  />
                  <PlaylistActionBar
                    playlistId={rightPlaylistId}
                    mode={rightPlaylistMode}
                    editable={rightEffectiveEditable}
                    onToggleEditable={setRightEditable}
                    onAddGraphic={handleOpenRightDrawer}
                    onEdit={handleEditRightPlaylist}
                  />
                </div>
                <Panel defaultSize={100}>
                  <Graphics playlistId={rightPlaylistId} editable={rightEffectiveEditable} />
                </Panel>
              </PanelGroup>
            </Panel>
          </CrossListDndProvider>

          <PanelResizeHandle />

          {/* Right column - Preview + Details */}
          <Panel defaultSize={36} minSize={25}>
            <PanelGroup direction="vertical">
              <StatusDescription
                rundown={data}
                mappedCanvases={getMappedCanvases(data.canvases, outputs)}
                isLive={data.isLive}
                className="border-b-[4px] border-[#151621]"
              />

              {/* Sandbox preview */}
              <Panel defaultSize={30}>
                <ViewWindow name="Sandbox" src={data.sandboxUrl} liveMode={false} />
              </Panel>

              <PanelResizeHandle />

              {/* PGM / Live preview */}
              {data.isLive && (
                <>
                  <Panel
                    defaultSize={37}
                    headerSize="large"
                    header={
                      <ToggleButtons
                        className="w-full justify-center px-2 -mt-0.5"
                        items={liveCanvases}
                        selectedItem={selectedCanvas}
                        onClickItem={(canvas) => setSelectedCanvas(canvas)}
                      />
                    }
                  >
                    {selectedCanvas?.output?.liveUrl ? (
                      <ViewWindow name="PGM" src={selectedCanvas.output.liveUrl} liveMode />
                    ) : (
                      <Text
                        as="p"
                        className="h-full !text-sm !text-gray-500 p-2 text-center content-center"
                      >
                        No output configured for this canvas
                      </Text>
                    )}
                  </Panel>

                  <PanelResizeHandle />
                </>
              )}

              {/* Graphic details / controls */}
              <Panel defaultSize={data.isLive ? 40 : 70}>
                {selectedPlaylistGraphic ? (
                  <GraphicDetails key={rundownId} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                    Select a graphic to view details
                  </div>
                )}
              </Panel>
            </PanelGroup>
          </Panel>
        </PanelGroup>
      </div>

      {/* Bottom quick control bar — single horizontal row */}
      <div className="shrink-0 bg-default-surface px-3 py-1.5 [&>div]:flex [&>div]:flex-row [&>div]:flex-nowrap [&>div]:overflow-x-auto [&>div]:gap-2">
        <QuickControl />
      </div>

      <Drawer
        isVisible={drawerTargetPlaylistId !== null}
        onClose={handleCloseDrawer}
        header="Graphics"
        className="overflow-auto h-full pb-4"
      >
        <GraphicList rundownId={rundownId} handleGraphicSelected={handleGraphicSelected} />
      </Drawer>
    </div>
  );
}

export default LayoutNrcsOperator;
